import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { Dialog, DialogTitle } from "@/components/ui/dialog";

const MAX_QUERY_LENGTH = 200;

type EnquirySearchLightboxProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const EnquirySearchLightbox: React.FC<EnquirySearchLightboxProps> = ({
  open,
  onOpenChange
}) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");

  // Reset and focus when opening; clear on close so the next open starts empty.
  useEffect(() => {
    if (open) {
      setQuery("");
      // Defer focus to the next frame so Radix has mounted the content.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  const handleSubmit = (): void => {
    const trimmed = query.trim();
    if (!trimmed) return;
    onOpenChange(false);
    navigate(`/enquiries?query=${encodeURIComponent(trimmed)}`);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const hasQuery = query.length > 0;
  const canSubmit = query.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Stronger backdrop than the default Dialog overlay — gives the
            lightbox a proper modal feel against the diary content. */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10000] bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-100 ease-out" />
        {/* Anchor near the top so the iOS keyboard has room beneath; centring
            the dialog vertically caused it to be clipped by the keyboard. */}
        {/* !-prefix on outline utilities promotes to !important to beat
            the global [data-theme="Dark"] [tabindex]:focus-visible rule
            in dark-theme-accessible.scss (Radix focuses the content when
            opening, which would otherwise paint a teal outline). */}
        <DialogPrimitive.Content className="fixed top-[10dvh] left-1/2 z-[30000] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border bg-background shadow-lg !outline-none focus:!outline-none focus-visible:!outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-100 ease-out">
          <DialogTitle className="sr-only">Search enquiries</DialogTitle>
          <div className="flex items-center gap-2 px-3 py-2">
            <Search
              size={18}
              aria-hidden="true"
              className="text-muted-foreground shrink-0"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search enquiries by name, email or reference"
              aria-label="Search enquiries"
              maxLength={MAX_QUERY_LENGTH}
              // !-prefixed utilities promote to !important to override
              // global rules: [data-theme="Dark"] input:focus-visible
              // (dark-theme-accessible.scss) paints an outline; iOS
              // WebKit and various dark-mode overrides paint an input
              // background. We want the input to seamlessly float in
              // the lightbox surface — no edge, no fill, no outline.
              className="flex-1 min-w-0 appearance-none !border-0 !bg-transparent text-base text-foreground placeholder:text-muted-foreground !outline-none focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0"
            />
            {hasQuery && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              aria-label="Search"
              className="shrink-0 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Search
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
};
